import {
  detectDelimiter,
  detectFixedWidthStarts,
  parseDelimited,
  parseFixedWidth,
  parseWhitespace,
} from "@/lib/tool-kit/text/delimited";

/**
 * Pull chosen columns out of delimited text: parse the input, resolve a column list
 * such as `3, 1, 5-7, Email` against the header row, then rename, filter, deduplicate
 * and sort the result.
 */

export type InputFormat =
  | "auto"
  | "comma"
  | "tab"
  | "semicolon"
  | "pipe"
  | "custom"
  | "whitespace"
  | "fixed";

export const INPUT_FORMATS: readonly InputFormat[] = [
  "auto",
  "comma",
  "tab",
  "semicolon",
  "pipe",
  "custom",
  "whitespace",
  "fixed",
];

const FORMAT_DELIMITERS: Partial<Record<InputFormat, string>> = {
  comma: ",",
  tab: "\t",
  semicolon: ";",
  pipe: "|",
};

export interface ParseInputOptions {
  format: InputFormat;
  /** For "custom": any string, such as " | " or "::". */
  customDelimiter: string;
  /** For "fixed": 0-based column starts; empty means detect them. */
  fixedStarts: readonly number[];
}

export interface ParsedInput {
  rows: string[][];
  /** The delimiter used, when the format has one; detected for "auto". */
  delimiter: string | null;
  unterminatedQuote: boolean;
  strayQuoteRows: number;
  /** The fixed-width column starts used (0-based). */
  fixedStarts: number[];
}

export function parseInput(
  text: string,
  options: ParseInputOptions
): ParsedInput {
  const base = {
    unterminatedQuote: false,
    strayQuoteRows: 0,
    fixedStarts: [] as number[],
  };
  if (options.format === "whitespace") {
    return { ...base, rows: parseWhitespace(text), delimiter: null };
  }
  if (options.format === "fixed") {
    const starts =
      options.fixedStarts.length > 0
        ? [...options.fixedStarts]
        : detectFixedWidthStarts(text);
    return {
      ...base,
      rows: parseFixedWidth(text, starts),
      delimiter: null,
      fixedStarts: starts,
    };
  }
  let delimiter = FORMAT_DELIMITERS[options.format] ?? null;
  if (options.format === "auto") {
    delimiter = detectDelimiter(text);
  } else if (options.format === "custom") {
    delimiter = options.customDelimiter || ",";
  }
  const parsed = parseDelimited(text, delimiter ?? ",");
  return {
    ...base,
    rows: parsed.rows,
    delimiter,
    unterminatedQuote: parsed.unterminatedQuote,
    strayQuoteRows: parsed.strayQuoteRows,
  };
}

/** The widest row's cell count. */
export function columnCount(rows: readonly (readonly string[])[]): number {
  return Math.max(0, ...rows.map((row) => row.length));
}

/* ---------------------------------------------------------------- the spec */

export interface ColumnSpec {
  /** 0-based column indexes, in output order. Repeats are allowed. */
  columns: number[];
  /** Tokens that matched no column number or header name. */
  unknown: string[];
}

const NUMBER = /^\d+$/;
const RANGE = /^(\d+)\s*-\s*(\d*)$/;
const DOUBLED_QUOTE = /""/g;

/** Split on commas that are not inside double quotes. */
function specTokens(spec: string): { text: string; quoted: boolean }[] {
  const tokens: { text: string; quoted: boolean }[] = [];
  let current = "";
  let quoted = false;
  let inQuotes = false;
  for (const character of spec) {
    if (character === '"') {
      inQuotes = !inQuotes;
      quoted = true;
      current += character;
    } else if (character === "," && !inQuotes) {
      tokens.push({ text: current.trim(), quoted });
      current = "";
      quoted = false;
    } else {
      current += character;
    }
  }
  tokens.push({ text: current.trim(), quoted });
  return tokens.filter((token) => token.text.length > 0);
}

function unquote(token: string): string {
  const trimmed = token.trim();
  const inner =
    trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2
      ? trimmed.slice(1, -1)
      : trimmed;
  return inner.replace(DOUBLED_QUOTE, '"').trim();
}

function rangeColumns(match: RegExpExecArray, count: number): number[] | null {
  const from = Number(match[1]);
  const to = match[2] === "" ? count : Math.min(Number(match[2]), count);
  if (from < 1 || from > count || to < 1) {
    return null;
  }
  const step = from <= to ? 1 : -1;
  const columns: number[] = [];
  for (let column = from; column !== to + step; column += step) {
    columns.push(column - 1);
  }
  return columns;
}

function headerIndex(
  name: string,
  headers: readonly string[] | null,
  locale: string
): number {
  if (!headers) {
    return -1;
  }
  const wanted = name.toLocaleLowerCase(locale);
  return headers.findIndex(
    (header) => header.trim().toLocaleLowerCase(locale) === wanted
  );
}

function headerIndexAsList(
  name: string,
  headers: readonly string[] | null,
  locale: string
): number[] | null {
  const index = headerIndex(name, headers, locale);
  return index === -1 ? null : [index];
}

/** Columns for one unquoted token: a number, a range, "*", or a header name. */
function tokenColumns(
  token: string,
  headers: readonly string[] | null,
  count: number,
  locale: string
): number[] | null {
  if (token === "*") {
    return Array.from({ length: count }, (_, index) => index);
  }
  if (NUMBER.test(token)) {
    const column = Number(token);
    return column >= 1 && column <= count ? [column - 1] : null;
  }
  const range = RANGE.exec(token);
  if (range) {
    return rangeColumns(range, count);
  }
  const index = headerIndex(token, headers, locale);
  return index === -1 ? null : [index];
}

/**
 * Resolve a column list against the table. Tokens are comma-separated column numbers
 * (1-based), ranges (`2-4`, `4-2` reversed, `3-` to the end), `*` for every column, or
 * header names matched without regard to case. Put a header name in double quotes when
 * it contains a comma or looks like a number. An empty spec keeps every column.
 */
export function parseColumnSpec(
  spec: string,
  headers: readonly string[] | null,
  count: number,
  locale = "en"
): ColumnSpec {
  const tokens = specTokens(spec);
  if (tokens.length === 0) {
    return {
      columns: Array.from({ length: count }, (_, index) => index),
      unknown: [],
    };
  }
  const columns: number[] = [];
  const unknown: string[] = [];
  for (const token of tokens) {
    const found = token.quoted
      ? headerIndexAsList(unquote(token.text), headers, locale)
      : tokenColumns(token.text, headers, count, locale);
    if (found) {
      columns.push(...found);
    } else {
      unknown.push(token.text);
    }
  }
  return { columns, unknown };
}

/* -------------------------------------------------------------- extraction */

export type SortDirection = "asc" | "desc";

export interface ExtractOptions {
  hasHeader: boolean;
  columns: readonly number[];
  /** New header names, by output position; blank keeps the original. */
  renames: readonly string[];
  /** The header for an output column with no name of its own (0-based input index). */
  fallbackName: (column: number) => string;
  trimCells: boolean;
  skipEmptyRows: boolean;
  dedupe: boolean;
  /** Output position to sort by, or -1 to keep the input order. */
  sortBy: number;
  sortDirection: SortDirection;
  locale: string;
}

export interface Extraction {
  /** The output header row, or null when the input has none and nothing was renamed. */
  header: string[] | null;
  rows: string[][];
  /** Data rows in the input. */
  inputRows: number;
  emptyRowsSkipped: number;
  duplicatesRemoved: number;
  /** Input rows too short for a selected column; the missing cells are blank. */
  shortRows: number;
}

function outputHeader(
  header: readonly string[] | null,
  options: ExtractOptions
): string[] | null {
  const renamed = options.renames.some((name) => name.trim().length > 0);
  if (!(header || renamed)) {
    return null;
  }
  return options.columns.map(
    (column, position) =>
      options.renames[position]?.trim() ||
      header?.[column]?.trim() ||
      options.fallbackName(column)
  );
}

function sortRows(rows: string[][], options: ExtractOptions): string[][] {
  if (options.sortBy < 0) {
    return rows;
  }
  const collator = new Intl.Collator(options.locale, {
    numeric: true,
    sensitivity: "base",
  });
  const sign = options.sortDirection === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = a[options.sortBy] ?? "";
    const right = b[options.sortBy] ?? "";
    // Blank cells go last whichever way the column is sorted.
    if (left === "" || right === "") {
      return Number(left === "") - Number(right === "");
    }
    return sign * collator.compare(left, right);
  });
}

export function extractColumns(
  table: readonly (readonly string[])[],
  options: ExtractOptions
): Extraction {
  const header = options.hasHeader ? (table[0] ?? null) : null;
  const data = options.hasHeader ? table.slice(1) : table;
  const widest = Math.max(-1, ...options.columns);
  let shortRows = 0;
  let emptyRowsSkipped = 0;
  const rows: string[][] = [];
  for (const row of data) {
    const cells = options.columns.map((column) => {
      const cell = row[column] ?? "";
      return options.trimCells ? cell.trim() : cell;
    });
    if (options.skipEmptyRows && cells.every((cell) => cell.trim() === "")) {
      emptyRowsSkipped++;
      continue;
    }
    if (row.length <= widest) {
      shortRows++;
    }
    rows.push(cells);
  }
  let kept = rows;
  if (options.dedupe) {
    const seen = new Set<string>();
    kept = rows.filter((row) => {
      const key = JSON.stringify(row);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }
  return {
    header: outputHeader(header, options),
    rows: sortRows(kept, options),
    inputRows: data.length,
    emptyRowsSkipped,
    duplicatesRemoved: rows.length - kept.length,
    shortRows,
  };
}
